/**
 * Widget / companion / control-trigger slice — Phase 8 commit 84.
 *
 * Owns: companion interaction mode, copilot-greeting flag, control-trigger
 * dedupe set, widget highlights (with auto-clear timers), and the
 * widget-context inspector (user→agent attachments + sent-context audit).
 */

/** @internal – import from `@/store/useDepartmentStore` instead. */
import type { StoreApi } from "zustand";
import type { CoAgentState } from "../types";

type SetState = StoreApi<CoAgentState>["setState"];
type GetState = StoreApi<CoAgentState>["getState"];

export const MAX_CONTROL_TRIGGER_KEYS = 240;

export type WidgetActions = Pick<
  CoAgentState,
  | "setCompanionMode"
  | "setCopilotGreetingSent"
  | "hasControlTriggerBeenSent"
  | "markControlTriggerSent"
  | "clearControlTriggersForSession"
  | "highlightWidget"
  | "clearWidgetHighlight"
  | "clearAllHighlights"
  | "attachWidgetContext"
  | "removeWidgetContext"
  | "clearWidgetContexts"
  | "recordSentContexts"
>;

export function createWidgetActions(set: SetState, get: GetState): WidgetActions {
  return {
    setCompanionMode: (mode) => set({ companionMode: mode }),
    setCopilotGreetingSent: (sent) => set({ copilotGreetingSent: sent }),

    hasControlTriggerBeenSent: (key) => {
      const cleanKey = key.trim();
      if (!cleanKey) return false;
      return get().sentControlTriggerKeys.includes(cleanKey);
    },

    markControlTriggerSent: (key) => {
      const cleanKey = key.trim();
      if (!cleanKey) return;
      set((state) => {
        if (state.sentControlTriggerKeys.includes(cleanKey)) return state;
        const next = [...state.sentControlTriggerKeys, cleanKey];
        return {
          sentControlTriggerKeys:
            next.length > MAX_CONTROL_TRIGGER_KEYS
              ? next.slice(next.length - MAX_CONTROL_TRIGGER_KEYS)
              : next,
        };
      });
    },

    clearControlTriggersForSession: (sessionId) => {
      const sid = sessionId.trim();
      if (!sid) return;
      set((state) => ({
        sentControlTriggerKeys: state.sentControlTriggerKeys.filter(
          (k) => !k.startsWith(`${sid}:`),
        ),
      }));
    },

    highlightWidget: (widgetId, comment, durationMs = 8000, actionLabel, stepIndex, onAction) => {
      const expiresAt = durationMs > 0 ? Date.now() + durationMs : 0;
      set((state) => ({
        widgetHighlights: [
          ...state.widgetHighlights.filter((h) => h.widgetId !== widgetId),
          { widgetId, comment, expiresAt, actionLabel, stepIndex, onAction },
        ],
      }));
      if (durationMs > 0) {
        setTimeout(() => {
          set((state) => ({
            widgetHighlights: state.widgetHighlights.filter(
              (h) => h.widgetId !== widgetId || h.expiresAt !== expiresAt,
            ),
          }));
        }, durationMs);
      }
    },

    clearWidgetHighlight: (widgetId) =>
      set((state) => ({
        widgetHighlights: state.widgetHighlights.filter((h) => h.widgetId !== widgetId),
      })),

    clearAllHighlights: () => set({ widgetHighlights: [] }),

    attachWidgetContext: (ctx) =>
      set((state) => {
        const existingIndex = state.attachedWidgetContexts.findIndex(
          (c) => c.widgetId === ctx.widgetId,
        );
        if (existingIndex >= 0) {
          const updated = [...state.attachedWidgetContexts];
          updated[existingIndex] = ctx;
          return { attachedWidgetContexts: updated };
        }
        if (state.attachedWidgetContexts.length >= 3) return state;
        return { attachedWidgetContexts: [...state.attachedWidgetContexts, ctx] };
      }),

    removeWidgetContext: (index) =>
      set((state) => ({
        attachedWidgetContexts: state.attachedWidgetContexts.filter((_, i) => i !== index),
      })),

    clearWidgetContexts: () => set({ attachedWidgetContexts: [] }),

    recordSentContexts: (messageId, contexts) =>
      set((state) => ({
        sentMessageContexts: { ...state.sentMessageContexts, [messageId]: contexts },
      })),
  };
}
